/**
 * Konva 9 `Node._getProtoListeners` 프로토타입 체인 중복 제거.
 *
 * Konva 9는 프로토타입 체인을 거슬러 올라가며 `obj.eventListeners[type]`을 단계마다 담는데, 자기 `eventListeners`가
 * 없는 중간 프로토타입(Stage·Container·Shape 등)은 `Node.prototype`의 것을 상속해 같은 리스너를 다시 담는다.
 * 그래서 Stage·Group·Rect의 `xChange`·`scaleXChange` 등에서 내장 핸들러(변환 캐시 무효화 + 하위 순회)가 3번 돈다.
 * 배치 안 된 변환 setter(핀치의 `stage.scaleX()`·`scaleY()`)마다 전체 트리 순회가 3회 → 1회가 되고,
 * 선택 중이면 Transformer.update()도 그만큼 준다(A21s 핀치: 프레임당 10회 → 6회).
 * Konva 10에서 같은 내용으로 고쳐졌다(protoListenerMap + seen Set). 내장 프로토타입 리스너는 전부 캐시 비우기라
 * 1회든 3회든 결과 상태는 같다.
 */
import Konva from 'konva'

interface ListenerEntry {
  name: string
  handler: (evt: unknown) => void
}
interface NodeInternals {
  _cache: Map<string, unknown>
  eventListeners?: Record<string, ListenerEntry[]>
}
type GetProtoListeners = (this: NodeInternals, eventType: string) => ListenerEntry[]

// Konva 원본은 'allEventListeners' 키에 캐시한다(on/off 때 지움). 프로토타입 리스너는 인스턴스 on/off로 바뀌지
// 않으므로 별도 키를 써도 되고, 그래야 원본 캐시와 섞이지 않는다
const CACHE_KEY = 'allEventListenersDedupe'
const nodeProto = Konva.Node.prototype as unknown as { _getProtoListeners: GetProtoListeners }

nodeProto._getProtoListeners = function (this: NodeInternals, eventType: string): ListenerEntry[] {
  const all = (this._cache.get(CACHE_KEY) as Record<string, ListenerEntry[]> | undefined) ?? {}
  let events = all[eventType]
  if (events === undefined) {
    events = []
    const seen = new Set<ListenerEntry>()
    let obj = Object.getPrototypeOf(this) as NodeInternals | null
    while (obj) {
      for (const entry of obj.eventListeners?.[eventType] ?? []) {
        if (seen.has(entry)) continue
        seen.add(entry)
        events.push(entry)
      }
      obj = Object.getPrototypeOf(obj) as NodeInternals | null
    }
    all[eventType] = events
    this._cache.set(CACHE_KEY, all)
  }
  return events
}
